import { showSurvey } from 'app/src/components/BusinessClassSurvey';
import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { forNamespace } from 'app/common/lib/util/i18n';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { Overlay, OverlayBackground } from 'app/src/components/Overlay';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { PlanSelection } from './PlanSelection';
import { FreeTrialPlanSelection } from './FreeTrialPlanSelection';
import { UpsellOverlay } from './UpsellOverlay';
import { useFreeTrialEligibilityRules } from './useFreeTrialEligibilityRules';
import { Analytics } from '@trello/atlassian-analytics';
import experiment from 'app/gamma/src/util/experiments';
import styles from './PlanSelectionOverlay.less';
import { IconName, Icon } from 'app/common/components/controls';
import { Spinner } from 'app/src/components/Spinner';
import RouterLink from 'app/common/components/singlespa/router-link';
import { usePlanSelectionOverlayQuery } from './PlanSelectionOverlayQuery.generated';

function usePrevious<T>(value: T) {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

const format = forNamespace('upgrade-prompt-plan-selection-consolidated');
const formatPrompt = forNamespace('upgrade prompt plan selection');

/**
 * PlanSeletionOverlay props
 *
 * orgId: gives the context of the org
 * onClose: callback to handle any clicks outside the overlay or on the 'x' button
 */
interface Props {
  orgId: string;
  onClose(): void;
}

export const PlanSelectionOverlay: React.FunctionComponent<Props> = ({
  onClose,
  orgId,
}) => {
  const { data, loading } = usePlanSelectionOverlayQuery(
    orgId
      ? {
          variables: {
            orgId,
          },
        }
      : { skip: true },
  );
  const teamName = data?.organization?.name;
  const meId = data?.member?.id;
  const isAdmin = data?.organization?.memberships?.find(
    (member) => member.idMember === meId && member.memberType === 'admin',
  );

  const {
    isEligible,
    isTrialActive,
    startFreeTrial,
    loading: freeTrialRulesLoading,
  } = useFreeTrialEligibilityRules(orgId);

  const [isViewingPlan, setIsViewingPlan] = useState(false);
  const [onFreeTrialLoading, setFreeTrialLoading] = useState(false);
  const isFreeTrialExistingEnabled = useFeatureFlag(
    'btg.free-trial-existing-teams',
    false,
  );
  const isBCTeamOnboardingEnabled = useFeatureFlag(
    'btg.bc-team-onboarding',
    false,
  );
  const showingFreeTrialPlanSelection =
    isEligible && isFreeTrialExistingEnabled;

  const onStartFreeTrial = async () => {
    if (onFreeTrialLoading) {
      return;
    }

    Analytics.sendTrackEvent({
      action: 'accepted',
      actionSubject: 'freeTrial',
      source: 'freeTrialModal',
      containers: {
        organization: {
          id: orgId,
        },
      },
    });

    setFreeTrialLoading(true);
    await startFreeTrial();
    showSurvey(orgId);
    setFreeTrialLoading(false);
  };

  // For BC Team Onboarding, we want to redirect to getting started
  // when they start their trial. This component will switch to showing
  // the plan selection component after trial starts, so this onClose
  // has the modal close and skip that step.
  const previousIsTrialActive = usePrevious(
    freeTrialRulesLoading ? undefined : !!isTrialActive,
  );
  useEffect(() => {
    const test = experiment.get('bcTeamOnboarding', orgId);
    if (
      isTrialActive &&
      previousIsTrialActive === false &&
      isBCTeamOnboardingEnabled &&
      test.get('variation') === 'enabled'
    ) {
      onClose();
    }
  }, [
    isBCTeamOnboardingEnabled,
    isTrialActive,
    previousIsTrialActive,
    onClose,
    orgId,
  ]);

  const goBackWithTracking = async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'backButton',
      source: 'planSelectionModal',
      containers: {
        organization: {
          id: orgId,
        },
      },
    });
    setIsViewingPlan(false);
  };

  const closeOverlayWithTracking = async () => {
    Analytics.sendClosedComponentEvent({
      componentType: 'modal',
      componentName: 'freeTrialModal',
      source: 'planSelectionModal',
    });
    onClose();
  };

  if (loading || freeTrialRulesLoading) {
    return null;
  }

  let children = (
    <PlanSelection
      cta={
        isAdmin ? (
          <RouterLink
            // Close the Plan Selection when redirecting to the /billing page
            onClick={() => onClose()}
            href={`/${teamName}/billing`}
            className={styles.planSelectionCtaButton}
          >
            {formatPrompt('start bc')}
          </RouterLink>
        ) : (
          <a className={styles.planSelectionCtaButton} href="/business-class">
            {formatPrompt('start bc')}
          </a>
        )
      }
    />
  );

  if (isFreeTrialExistingEnabled) {
    if (isEligible) {
      if (isViewingPlan) {
        children = (
          <FreeTrialPlanSelection
            type="try-business-class"
            orgId={orgId}
            cta={
              <button
                className={styles.freeTrialPlanSelectionCtaButton}
                onClick={onStartFreeTrial}
              >
                {onFreeTrialLoading ? (
                  <Spinner centered small wrapperClassName={styles.spinner} />
                ) : (
                  format(['try-business-class', 'cta'], {
                    dayCount: 14,
                  })
                )}
              </button>
            }
            trialLength={14}
          />
        );
      } else {
        children = (
          <UpsellOverlay
            onCtaClick={onStartFreeTrial}
            isLoading={onFreeTrialLoading}
            orgId={orgId}
            onLearnMore={() => setIsViewingPlan(true)}
          />
        );
      }
    } else if (isTrialActive) {
      children = (
        <FreeTrialPlanSelection
          type="have-business-class"
          cta={
            <a
              className={styles.freeTrialPlanSelectionCtaButton}
              href="/business-class"
            >
              {formatPrompt('learn bc')}
            </a>
          }
          orgId={orgId}
          trialLength={14}
        />
      );
    }
  }

  return (
    <Overlay onClose={onClose} background={OverlayBackground.DARK}>
      <div
        className={classNames(
          styles.animation,
          styles.overlay,
          isFreeTrialExistingEnabled && styles.overlayExistingTeams,
          (isTrialActive || isViewingPlan) && styles.overlayViewingPlan,
          showingFreeTrialPlanSelection && isViewingPlan && styles.grow,
          showingFreeTrialPlanSelection && !isViewingPlan && styles.shrink,
        )}
        tabIndex={-1}
        ref={(el) => {
          if (el) {
            // bring focus to overlay container
            el.focus();
          }
        }}
      >
        {isFreeTrialExistingEnabled && isViewingPlan && (
          <button
            className={styles.back}
            title="Todo"
            aria-label="Todo"
            onClick={() => goBackWithTracking()}
          >
            <Icon name={IconName.Back} large />
          </button>
        )}
        <button
          className={styles.close}
          title={formatPrompt('close')}
          aria-label={formatPrompt('close')}
          onClick={() => closeOverlayWithTracking()}
        >
          <Icon name={IconName.Close} large />
        </button>
        {children}
      </div>
    </Overlay>
  );
};

export const PlanSelectionOverlayConnected: React.FunctionComponent<Props> = (
  props,
) => (
  <ComponentWrapper>
    <PlanSelectionOverlay {...props} />
  </ComponentWrapper>
);
